import {
  LineItem,
  EquipmentItem,
  PVWireItem,
  MaterialItem,
  NecCalculatorConfig,
  SingleItemTotals,
  ItemLocationDetail,
} from '../types';
import { calculateSingleItemTotals } from './calculations';
import { parseSafeNumber } from '../utils/helpers';

export const DEFAULT_NEC_CONFIG: NecCalculatorConfig = {
  wasteFactorPercent: 10,
  conduitStickLengthFt: 10,
  enclosureEntryType: 'AUTO',
  includeChannelHardware: true,
  addPullBoxesForLongRuns: true,
};

/**
 * Returns max support spacing (in feet) per NEC code requirements.
 */
export function getNecMaxSupportInterval(conduitType: string, conduitSize: string): number {
  const normType = conduitType.toUpperCase().trim();
  const normSize = conduitSize.trim();

  // PVC - NEC 352.30 & Table 352.30
  if (normType.includes('PVC')) {
    if (normSize.includes('1/2') || normSize.includes('3/4') || normSize === '1' || normSize === '1"') {
      return 3;
    }
    if (normSize.includes('1-1/4') || normSize.includes('1-1/2') || normSize === '2' || normSize === '2"') {
      return 5;
    }
    if (normSize.includes('2-1/2') || normSize === '3' || normSize === '3"') {
      return 6;
    }
    return 7; // 3-1/2" - 5"
  }

  // Liquidtight Flexible Metal Conduit LFMC / FMC - NEC 350.30 / 348.30
  if (normType.includes('FMC') || normType.includes('FLEX') || normType.includes('LFMC')) {
    return 4.5;
  }

  // RMC / IMC - NEC 344.30
  if (normType.includes('RMC') || normType.includes('IMC') || normType.includes('RIGID')) {
    return 10;
  }

  // EMT - NEC 358.30: Support intervals not exceeding 10 ft
  return 10;
}

/**
 * Calculates strut straps required for a single conduit run according to NEC spacing:
 * Securely fastened within 3 feet of each box, cabinet, or conduit termination,
 * and at intervals not exceeding NEC table maximums.
 */
export function calculateRunStrutStraps(
  lengthFt: number,
  conduitType: string,
  conduitSize: string
): { count: number; endStraps: number; intermediateStraps: number; rule: string } {
  if (lengthFt <= 0) return { count: 0, endStraps: 0, intermediateStraps: 0, rule: 'Zero length run' };

  const normType = conduitType.toUpperCase();
  const maxInterval = getNecMaxSupportInterval(conduitType, conduitSize);

  let endStraps = 2;
  let intermediateStraps = 0;
  let ruleText = '';

  if (normType.includes('PVC')) {
    if (lengthFt <= 3) {
      endStraps = 1;
      intermediateStraps = 0;
    } else if (lengthFt <= 6) {
      endStraps = 2;
      intermediateStraps = 0;
    } else {
      endStraps = 2;
      intermediateStraps = Math.max(0, Math.ceil((lengthFt - 6) / maxInterval));
    }
    ruleText = `NEC 352.30 (Within 3ft of terminations, max ${maxInterval}ft intervals)`;
  } else if (normType.includes('FMC') || normType.includes('LFMC')) {
    if (lengthFt <= 2) {
      endStraps = 1;
      intermediateStraps = 0;
    } else {
      endStraps = 2;
      intermediateStraps = Math.max(0, Math.ceil((lengthFt - 2) / 4.5));
    }
    ruleText = `NEC 350.30 (Within 12" of terminations, max 4.5ft intervals)`;
  } else {
    // EMT (NEC 358.30) & RMC (NEC 344.30)
    if (lengthFt <= 3) {
      endStraps = 1;
      intermediateStraps = 0;
    } else if (lengthFt <= 6) {
      endStraps = 2;
      intermediateStraps = 0;
    } else {
      endStraps = 2;
      intermediateStraps = Math.max(0, Math.ceil((lengthFt - 6) / 10));
    }
    ruleText = `NEC 358.30(A) / 344.30(A) (Within 3ft of boxes, max 10ft intervals)`;
  }

  return {
    count: endStraps + intermediateStraps,
    endStraps,
    intermediateStraps,
    rule: ruleText,
  };
}

/**
 * Calculates conduit couplings needed for a run of length L (based on standard 10ft conduit sticks).
 */
export function calculateRunCouplings(
  lengthFt: number,
  conduitStickLengthFt = 10
): number {
  if (lengthFt <= conduitStickLengthFt) return 0;
  return Math.max(0, Math.ceil(lengthFt / conduitStickLengthFt) - 1);
}

/**
 * Checks if an equipment enclosure name/spec is outdoor/raintight (NEMA 3R, 4, 4X, etc.).
 */
export function isOutdoorEnclosure(enclosureText?: string): boolean {
  if (!enclosureText) return true; // Default to outdoor for solar installations
  const lower = enclosureText.toLowerCase();
  if (lower.includes('1') && !lower.includes('12') && !lower.includes('13')) {
    // NEMA 1 indoor dry
    return false;
  }
  return (
    lower.includes('3r') ||
    lower.includes('3') ||
    lower.includes('4') ||
    lower.includes('4x') ||
    lower.includes('12') ||
    lower.includes('outdoor') ||
    lower.includes('weatherproof') ||
    lower.includes('raintight') ||
    lower.includes('nema 3')
  );
}

/**
 * Generates the full Material List (BOM) based on extracted line items, equipment,
 * and NEC regulatory rules for straps, couplings, and connectors.
 * Every material item includes a comprehensive derivation explanation and specific
 * installation location breakdown.
 */
export function generateBillOfMaterials(
  lineItems: LineItem[],
  equipmentItems: EquipmentItem[] = [],
  pvWireItems: PVWireItem[] = [],
  config: NecCalculatorConfig = DEFAULT_NEC_CONFIG
): MaterialItem[] {
  const bom: MaterialItem[] = [];
  const wasteMultiplier = 1 + (config.wasteFactorPercent || 0) / 100;

  // Equipment lookup map to find enclosure type
  const equipmentEnclosureMap: Record<string, string> = {};
  for (const eq of equipmentItems) {
    const name = (eq['Equipment'] || eq['Description'] || '').toLowerCase();
    const enc = eq['Enclosure'] || 'NEMA 3R';
    if (name) {
      equipmentEnclosureMap[name] = enc;
    }
  }

  const findEnclosure = (circuitName?: string): string => {
    if (!circuitName) return 'NEMA 3R';
    const lower = circuitName.toLowerCase();
    for (const [key, enc] of Object.entries(equipmentEnclosureMap)) {
      if (lower.includes(key)) return enc;
    }
    // Solar equipment defaults
    if (lower.includes('inverter') || lower.includes('combiner') || lower.includes('disconnect') || lower.includes('transformer')) {
      return 'NEMA 3R';
    }
    if (lower.includes('switchboard') || lower.includes('main switch') || lower.includes('mdp')) {
      return 'NEMA 1';
    }
    return 'NEMA 3R';
  };

  // Aggregation maps with location tracking
  interface AggregateData {
    qty: number;
    spec: string;
    tradeSize: string;
    locations: ItemLocationDetail[];
    necCitation: string;
    detailsText: string[];
  }

  const wireAggregates: Record<string, AggregateData> = {};
  const conduitAggregates: Record<string, AggregateData & { conduitType: string }> = {};
  const strapAggregates: Record<string, AggregateData & { conduitType: string }> = {};
  const couplingAggregates: Record<string, AggregateData & { conduitType: string; isOutdoor: boolean }> = {};
  const connectorAggregates: Record<string, AggregateData & { connectorType: string; enclosure: string }> = {};

  let totalSupportLocations = 0;
  const hardwareLocations: ItemLocationDetail[] = [];

  // Process wire schedule runs
  for (let i = 0; i < lineItems.length; i++) {
    const item = lineItems[i];
    const acDc = (item['AC_DC'] || '').toLowerCase();
    const origin = item['Circuit_Origin'] || `Origin #${i + 1}`;
    const dest = item['Circuit_Destination'] || `Destination #${i + 1}`;
    const runLabel = `Run #${i + 1}: ${origin} \u2192 ${dest}`;

    // Skip pure DC strings handled by pvWireItems
    if (acDc.includes('dc') && (origin.toLowerCase().includes('module') || dest.toLowerCase().includes('inverter'))) {
      continue;
    }

    const calc: SingleItemTotals = calculateSingleItemTotals(item);
    if (!calc.tradeSize || calc.length <= 0) continue;

    // 1. Conductor Wire Totals
    const wireKey = `${calc.tradeSize} Cu THHN/THWN-2`;
    const runConductorBase = calc.wireTotal;
    const runConductorWithWaste = Math.round(runConductorBase * wasteMultiplier);

    if (!wireAggregates[wireKey]) {
      wireAggregates[wireKey] = {
        qty: 0,
        spec: '600V Copper 90°C Wet/Dry Rated Conductors',
        tradeSize: calc.tradeSize,
        locations: [],
        necCitation: 'NEC 310.16 (Conductor Ampacity & Insulation Rating)',
        detailsText: [],
      };
    }
    wireAggregates[wireKey].qty += runConductorWithWaste;
    wireAggregates[wireKey].locations.push({
      location: runLabel,
      quantity: runConductorWithWaste,
      purpose: `${calc.wireQuantity} phase/hot conductors (${calc.length} ft x ${calc.parallel} parallel${calc.inverterMultiplier > 1 ? ` x ${calc.inverterMultiplier} inverters` : ''}) + ${config.wasteFactorPercent}% pull margin`,
      runDescription: runLabel,
      necCitation: 'NEC 310.16',
    });
    wireAggregates[wireKey].detailsText.push(
      `${runLabel}: ${calc.wireQuantity} conductors x ${calc.length} ft = ${runConductorBase} ft (+${config.wasteFactorPercent}% margin = ${runConductorWithWaste} ft)`
    );

    // Ground conductor (EGC)
    if (calc.groundAdder === 1 && calc.groundSize) {
      const groundKey = `${calc.groundSize} Cu Bare/Green Ground (EGC)`;
      const groundBase = calc.groundTotal;
      const groundWithWaste = Math.round(groundBase * wasteMultiplier);

      if (!wireAggregates[groundKey]) {
        wireAggregates[groundKey] = {
          qty: 0,
          spec: 'Equipment Grounding Conductor (EGC) per NEC 250.122',
          tradeSize: calc.groundSize,
          locations: [],
          necCitation: 'NEC 250.122 (Equipment Grounding Conductors)',
          detailsText: [],
        };
      }
      wireAggregates[groundKey].qty += groundWithWaste;
      wireAggregates[groundKey].locations.push({
        location: runLabel,
        quantity: groundWithWaste,
        purpose: `1x ${calc.groundSize} Equipment Grounding Conductor along ${calc.length} ft run (+${config.wasteFactorPercent}% margin)`,
        runDescription: runLabel,
        necCitation: 'NEC 250.122',
      });
      wireAggregates[groundKey].detailsText.push(
        `${runLabel}: 1 ground x ${calc.length} ft = ${groundBase} ft (+${config.wasteFactorPercent}% margin = ${groundWithWaste} ft)`
      );
    }

    // 2. Conduit Totals
    if (calc.conduitSize && calc.conduitType) {
      const conduitKey = `${calc.conduitSize}" ${calc.conduitType}`;
      const runConduitBase = calc.conduitLength;
      const runConduitWithWaste = Math.round(runConduitBase * wasteMultiplier);
      const sticks = Math.ceil(runConduitWithWaste / config.conduitStickLengthFt);

      if (!conduitAggregates[conduitKey]) {
        conduitAggregates[conduitKey] = {
          qty: 0,
          conduitType: calc.conduitType,
          tradeSize: `${calc.conduitSize}"`,
          spec: `Standard 10-foot sticks (${calc.conduitType})`,
          locations: [],
          necCitation: calc.conduitType.includes('EMT')
            ? 'NEC Article 358'
            : calc.conduitType.includes('PVC')
            ? 'NEC Article 352'
            : 'NEC Article 344',
          detailsText: [],
        };
      }
      conduitAggregates[conduitKey].qty += runConduitWithWaste;
      conduitAggregates[conduitKey].locations.push({
        location: runLabel,
        quantity: runConduitWithWaste,
        purpose: `Conduit raceway for ${calc.wireQuantity}x ${calc.tradeSize} conductors across ${calc.length} ft (${sticks} sticks of 10ft)`,
        runDescription: runLabel,
        necCitation: conduitAggregates[conduitKey].necCitation,
      });
      conduitAggregates[conduitKey].detailsText.push(
        `${runLabel}: ${calc.length} ft run x ${calc.parallel} parallel = ${runConduitBase} ft (+${config.wasteFactorPercent}% margin = ${runConduitWithWaste} ft / ${sticks} sticks)`
      );

      // 3. Strut Straps per NEC Spacing
      const strapResult = calculateRunStrutStraps(calc.length, calc.conduitType, calc.conduitSize);
      const totalStrapsForRun = strapResult.count * calc.parallel * calc.inverterMultiplier;
      totalSupportLocations += totalStrapsForRun;

      const strapKey = `${calc.conduitSize}" ${calc.conduitType}`;
      if (!strapAggregates[strapKey]) {
        strapAggregates[strapKey] = {
          qty: 0,
          tradeSize: `${calc.conduitSize}"`,
          conduitType: calc.conduitType,
          spec: 'Two-piece zinc-plated slotted steel strut clamp with hex bolt and nut',
          locations: [],
          necCitation: strapResult.rule,
          detailsText: [],
        };
      }
      strapAggregates[strapKey].qty += totalStrapsForRun;
      strapAggregates[strapKey].locations.push({
        location: runLabel,
        quantity: totalStrapsForRun,
        purpose: `${strapResult.endStraps} end straps within 3ft of boxes (${origin} & ${dest}) + ${strapResult.intermediateStraps} intermediate strap(s) every 10ft`,
        runDescription: runLabel,
        necCitation: strapResult.rule,
      });
      strapAggregates[strapKey].detailsText.push(
        `${runLabel}: ${totalStrapsForRun} straps (${strapResult.endStraps} within 3ft of terminations + ${strapResult.intermediateStraps} along ${calc.length} ft run)`
      );

      hardwareLocations.push({
        location: runLabel,
        quantity: totalStrapsForRun,
        purpose: `Support hardware points along ${calc.length} ft raceway`,
        runDescription: runLabel,
        necCitation: 'NEC 300.11',
      });

      // 4. Couplings per 10ft Stick
      const originEnc = findEnclosure(origin);
      const destEnc = findEnclosure(dest);
      const isOutdoorRun = isOutdoorEnclosure(originEnc) || isOutdoorEnclosure(destEnc);

      const couplingsPerSingleRun = calculateRunCouplings(calc.length, config.conduitStickLengthFt);
      const totalCouplingsForRun = couplingsPerSingleRun * calc.parallel * calc.inverterMultiplier;

      if (totalCouplingsForRun > 0) {
        const couplingKey = `${calc.conduitSize}" ${calc.conduitType} (${isOutdoorRun ? 'Raintight' : 'Dry'})`;
        if (!couplingAggregates[couplingKey]) {
          couplingAggregates[couplingKey] = {
            qty: 0,
            tradeSize: `${calc.conduitSize}"`,
            conduitType: calc.conduitType,
            isOutdoor: isOutdoorRun,
            spec: isOutdoorRun
              ? 'UL Listed Raintight (wet locations) with internal sealing gland'
              : 'Steel construction, concrete-tight when taped',
            locations: [],
            necCitation: isOutdoorRun
              ? 'NEC 314.15 & 358.42 (Listed Wet Location Couplings)'
              : 'NEC 358.42 (EMT Couplings)',
            detailsText: [],
          };
        }
        couplingAggregates[couplingKey].qty += totalCouplingsForRun;
        couplingAggregates[couplingKey].locations.push({
          location: runLabel,
          quantity: totalCouplingsForRun,
          purpose: `Coupling joint transitions between ${Math.ceil(calc.length / config.conduitStickLengthFt)} conduit sticks across ${calc.length} ft`,
          runDescription: runLabel,
          necCitation: couplingAggregates[couplingKey].necCitation,
        });
        couplingAggregates[couplingKey].detailsText.push(
          `${runLabel}: ${totalCouplingsForRun} couplings (${Math.ceil(calc.length / config.conduitStickLengthFt)} sticks of 10ft)`
        );
      }

      // 5. Connectors at Enclosure Terminations (2 terminations per conduit run)
      const terminals = [
        {
          name: origin,
          enclosure: originEnc,
          role: 'Feeder Origin Termination',
          desc: `Knockout connection at ${origin} (${originEnc})`,
        },
        {
          name: dest,
          enclosure: destEnc,
          role: 'Feeder Destination Termination',
          desc: `Knockout connection at ${dest} (${destEnc})`,
        },
      ];

      for (const term of terminals) {
        const outdoor = isOutdoorEnclosure(term.enclosure);
        const normConduit = calc.conduitType.toUpperCase();
        let connectorType = '';
        let necCitation = '';

        if (normConduit.includes('PVC')) {
          connectorType = 'PVC Male Terminal Adapter w/ Neoprene Sealing O-Ring & Locknut';
          necCitation = 'NEC 352.46 & 314.15 (Wet Location Entry)';
        } else if (outdoor) {
          if (config.enclosureEntryType === 'TOP_SIDE' || config.enclosureEntryType === 'AUTO') {
            connectorType = 'Myers Watertight Conduit Hub w/ Grounding Screw';
            necCitation = 'NEC 312.2 & 314.15 (Weatherproof Enclosure Hub)';
          } else {
            connectorType = 'EMT Raintight Compression Connector w/ Insulated Throat';
            necCitation = 'NEC 358.42 & 314.15 (Listed Raintight)';
          }
        } else {
          connectorType = 'EMT Steel Set-Screw Connector w/ Insulated Throat';
          necCitation = 'NEC 358.42 & 300.4 (Insulated Throat Protection)';
        }

        const connKey = `${calc.conduitSize}" - ${connectorType}`;
        const countToAdd = 1 * calc.parallel * calc.inverterMultiplier;

        if (!connectorAggregates[connKey]) {
          connectorAggregates[connKey] = {
            qty: 0,
            tradeSize: `${calc.conduitSize}"`,
            connectorType,
            enclosure: term.enclosure,
            spec: connectorType.includes('Myers')
              ? 'Die-cast zinc with Viton O-ring seal, insulated throat, and ground lug'
              : 'Steel body with insulated nylon throat protector and sealing ring',
            locations: [],
            necCitation,
            detailsText: [],
          };
        }
        connectorAggregates[connKey].qty += countToAdd;
        connectorAggregates[connKey].locations.push({
          location: `${term.name} (${term.enclosure})`,
          quantity: countToAdd,
          purpose: `${term.desc} for ${runLabel}`,
          runDescription: runLabel,
          necCitation,
        });
        connectorAggregates[connKey].detailsText.push(
          `${countToAdd}x at ${term.name} (${term.enclosure}) for ${runLabel}`
        );

        // Grounding / Bonding Bushings for conduits >= 1-1/4" or service equipment
        const sizeNum = parseSafeNumber(calc.conduitSize.split('/')[0]) || 1;
        if (sizeNum >= 1.25 || term.name.toLowerCase().includes('switchboard') || term.name.toLowerCase().includes('service')) {
          const bushingKey = `${calc.conduitSize}" Insulated Grounding Bonding Bushing w/ Lay-In Lug`;
          if (!connectorAggregates[bushingKey]) {
            connectorAggregates[bushingKey] = {
              qty: 0,
              tradeSize: `${calc.conduitSize}"`,
              connectorType: 'Bonding Bushing w/ Lug',
              enclosure: term.enclosure,
              spec: 'Malleable iron with tin-plated copper lay-in grounding lug (150°C)',
              locations: [],
              necCitation: 'NEC 250.92 & 250.97 (Service/Over 250V Bonding)',
              detailsText: [],
            };
          }
          connectorAggregates[bushingKey].qty += countToAdd;
          connectorAggregates[bushingKey].locations.push({
            location: `${term.name} (${term.enclosure})`,
            quantity: countToAdd,
            purpose: `Bonding conductor lug grounding bushing at ${term.name} knockout for ${runLabel}`,
            runDescription: runLabel,
            necCitation: 'NEC 250.92 & 250.97',
          });
          connectorAggregates[bushingKey].detailsText.push(
            `${countToAdd}x at ${term.name} (${term.enclosure})`
          );
        }
      }
    }
  }

  // Process PV Wire Strings
  if (pvWireItems && pvWireItems.length > 0) {
    let totalPvWireFt = 0;
    const pvWireSpec = pvWireItems[0]?.pv_wire_size || '#10 AWG PV Wire 2kV XLPE';
    const pvLocations: ItemLocationDetail[] = [];
    const pvDetailsText: string[] = [];

    pvWireItems.forEach((pv, idx) => {
      const dist = parseSafeNumber(pv.max_distance_ft || pv.Distance);
      const runFt = dist * 2;
      totalPvWireFt += runFt;
      const mppt = pv.mppt ? ` (MPPT ${pv.mppt})` : '';
      pvLocations.push({
        location: `Solar Array String #${idx + 1}${mppt}`,
        quantity: Math.round(runFt * wasteMultiplier),
        purpose: `Positive & negative DC home-run pair (${dist} ft distance)`,
        runDescription: `PV String #${idx + 1} to Inverter`,
        necCitation: 'NEC 690.31',
      });
      pvDetailsText.push(`String #${idx + 1}${mppt}: ${dist} ft x 2 = ${runFt} ft (+10% = ${Math.round(runFt * wasteMultiplier)} ft)`);
    });

    if (totalPvWireFt > 0) {
      const pvWireWithWaste = Math.round(totalPvWireFt * wasteMultiplier);
      wireAggregates[pvWireSpec] = {
        qty: pvWireWithWaste,
        spec: 'UL 4703 2000V Sunlight Resistant Photovoltaic Wire',
        tradeSize: '#10 AWG',
        locations: pvLocations,
        necCitation: 'NEC 690.31 (Methods Permitted in PV Systems)',
        detailsText: pvDetailsText,
      };
    }
  }

  // --- ASSEMBLE FINAL BOM ITEMS ---

  // 1. Wire Items
  Object.entries(wireAggregates).forEach(([wireDesc, data], index) => {
    const derivation = `${data.qty.toLocaleString()} ft obtained from ${data.locations.length} circuit runs in the wire schedule plus ${config.wasteFactorPercent}% pull & makeup allowance: ${data.detailsText.slice(0, 3).join('; ')}${data.detailsText.length > 3 ? ` ...and ${data.detailsText.length - 3} more` : ''}.`;

    bom.push({
      id: `wire-${index}`,
      category: 'WIRE',
      description: wireDesc,
      tradeSize: data.tradeSize,
      specification: data.spec,
      quantity: data.qty,
      unit: 'ft',
      isAutoGenerated: true,
      necReference: data.necCitation,
      quantityDerivation: derivation,
      locations: data.locations,
      notes: `Includes ${config.wasteFactorPercent}% pull & trim allowance across ${data.locations.length} circuits`,
    });
  });

  // 2. Conduit Items
  Object.entries(conduitAggregates).forEach(([conduitDesc, data], index) => {
    const totalSticks = Math.ceil(data.qty / config.conduitStickLengthFt);
    const derivation = `${data.qty.toLocaleString()} ft (${totalSticks} sticks of 10ft) obtained from ${data.locations.length} conduit runs: ${data.detailsText.slice(0, 3).join('; ')}${data.detailsText.length > 3 ? ` ...and ${data.detailsText.length - 3} more` : ''}. Includes ${config.wasteFactorPercent}% cutting allowance.`;

    bom.push({
      id: `conduit-${index}`,
      category: 'CONDUIT',
      description: `${data.tradeSize} ${data.conduitType} Conduit`,
      tradeSize: data.tradeSize,
      specification: data.spec,
      quantity: data.qty,
      unit: 'ft',
      isAutoGenerated: true,
      necReference: data.necCitation,
      quantityDerivation: derivation,
      locations: data.locations,
      notes: `${totalSticks} sticks (10ft standard lengths)`,
    });
  });

  // 3. Strut Straps (Conduit Supports per NEC)
  Object.entries(strapAggregates).forEach(([strapDesc, data], index) => {
    const derivation = `${data.qty} strut straps calculated per NEC strapping rules: 2 end straps within 3 ft of each enclosure termination, plus intermediate straps spaced every 10 ft along each run. Derived across ${data.locations.length} raceways: ${data.detailsText.slice(0, 3).join('; ')}${data.detailsText.length > 3 ? ` ...and ${data.detailsText.length - 3} more` : ''}.`;

    bom.push({
      id: `strap-${index}`,
      category: 'STRUT_STRAP',
      description: `${data.tradeSize} Heavy-Duty Strut Strap (${data.conduitType})`,
      tradeSize: data.tradeSize,
      specification: data.spec,
      quantity: data.qty,
      unit: 'ea',
      isAutoGenerated: true,
      necReference: data.necCitation,
      enclosureCompatibility: 'Mounts directly to standard 1-5/8" unistrut channel',
      quantityDerivation: derivation,
      locations: data.locations,
      notes: 'NEC 358.30 / 344.30 compliant support strapping',
    });
  });

  // 4. Couplings
  Object.entries(couplingAggregates).forEach(([couplingDesc, data], index) => {
    const derivation = `${data.qty} couplings obtained based on 10ft conduit stick transitions (formula: sticks - 1 per run): ${data.detailsText.slice(0, 3).join('; ')}${data.detailsText.length > 3 ? ` ...and ${data.detailsText.length - 3} more` : ''}.`;

    bom.push({
      id: `coupling-${index}`,
      category: 'COUPLING',
      description: `${data.tradeSize} ${data.conduitType} ${data.isOutdoor ? 'Raintight Compression' : 'Set-Screw'} Coupling`,
      tradeSize: data.tradeSize,
      specification: data.spec,
      quantity: data.qty,
      unit: 'ea',
      isAutoGenerated: true,
      necReference: data.necCitation,
      quantityDerivation: derivation,
      locations: data.locations,
      notes: '10ft conduit segment transitions',
    });
  });

  // 5. Connectors & Enclosure Hubs
  Object.entries(connectorAggregates).forEach(([connDesc, data], index) => {
    const isMyers = data.connectorType.includes('Myers');
    const isBushing = data.connectorType.includes('Bushing');
    const derivation = `${data.qty} total fittings obtained for ${data.locations.length} enclosure knockout entries. Terminating at: ${data.detailsText.slice(0, 4).join('; ')}${data.detailsText.length > 4 ? ` ...and ${data.detailsText.length - 4} more` : ''}.`;

    bom.push({
      id: `connector-${index}`,
      category: 'CONNECTOR',
      description: `${data.tradeSize} ${data.connectorType}`,
      tradeSize: data.tradeSize,
      specification: data.spec,
      quantity: data.qty,
      unit: 'ea',
      isAutoGenerated: true,
      necReference: data.necCitation,
      enclosureCompatibility: `Engineered for ${data.enclosure} enclosure entry points`,
      quantityDerivation: derivation,
      locations: data.locations,
      notes: isMyers
        ? 'Mandated for weatherproof outdoor enclosure entries'
        : isBushing
        ? 'Required for service raceways & high voltage bonding'
        : 'UL Listed termination connector',
    });
  });

  // 6. Support Hardware & Unistrut Channel
  if (config.includeChannelHardware && totalSupportLocations > 0) {
    const unistrutFt = Math.max(10, Math.ceil(totalSupportLocations * 1.5));
    bom.push({
      id: 'hardware-unistrut',
      category: 'HARDWARE',
      description: '1-5/8" x 1-5/8" 12-Gauge Galvanized Unistrut Channel',
      tradeSize: '1-5/8"',
      specification: 'Pre-galvanized slotted steel channel (10ft lengths)',
      quantity: unistrutFt,
      unit: 'ft',
      isAutoGenerated: true,
      necReference: 'NEC 300.11 (Securing and Supporting)',
      quantityDerivation: `${unistrutFt} linear feet derived from ${totalSupportLocations} conduit support locations at 1.5 ft per rack point: supports all conduit runs along the electrical routing path.`,
      locations: hardwareLocations,
      notes: `Supports ${totalSupportLocations} calculated conduit strapping points`,
    });

    bom.push({
      id: 'hardware-channel-nuts',
      category: 'HARDWARE',
      description: '3/8"-16 Strut Channel Spring Nuts & Grade 5 Hex Cap Screws',
      tradeSize: '3/8"-16',
      specification: 'Zinc electroplated with spring, 1" hex bolts and flat washers',
      quantity: totalSupportLocations * 2,
      unit: 'sets',
      isAutoGenerated: true,
      necReference: 'NEC 300.11',
      quantityDerivation: `${totalSupportLocations * 2} hardware sets derived from 2 sets (spring nut + hex bolt + washer) per each of the ${totalSupportLocations} strut strap support points.`,
      locations: hardwareLocations,
      notes: 'Hardware to mount conduit strut clamps and bracket assemblies',
    });
  }

  // 7. Equipment Items (Major One-Line Equipment)
  equipmentItems.forEach((eq, index) => {
    const qty = parseSafeNumber(eq['Quantity']) || 1;
    const name = eq['Equipment'] || eq['Description'] || `Equipment #${index + 1}`;
    const enc = eq['Enclosure'] || 'NEMA 3R';
    const loc: ItemLocationDetail = {
      location: `Equipment Pad / Electrical Wall: ${name}`,
      quantity: qty,
      purpose: `${eq['Description'] || name} (${enc}, ${eq['Rating'] || ''})`,
      necCitation: eq['Equipment']?.toLowerCase().includes('disconnect') ? 'NEC 690.13' : 'NEC 690.8',
    };

    bom.push({
      id: `equipment-${index}`,
      category: 'EQUIPMENT',
      description: name,
      tradeSize: eq['Rating'] || eq['Voltage'] || 'Standard',
      specification: `${eq['Description'] || ''} | Enclosure: ${enc} | Rating: ${eq['Rating'] || 'N/A'}`,
      quantity: qty,
      unit: 'ea',
      isAutoGenerated: false,
      necReference: eq['Equipment']?.toLowerCase().includes('disconnect')
        ? 'NEC 690.13 / 690.15 (PV Disconnecting Means)'
        : eq['Equipment']?.toLowerCase().includes('inverter')
        ? 'NEC 690.8 / 690.9'
        : 'NEC Article 705',
      enclosureCompatibility: enc,
      quantityDerivation: `${qty} unit(s) extracted from plan equipment schedule for ${name} (${enc} enclosure rating).`,
      locations: [loc],
      notes: `Status: ${eq['Status'] || 'New Equipment'}`,
    });
  });

  return bom;
}
